import { DateTime } from 'luxon';
import { Carriers } from 'smg/customer-api/carriers';
import * as models from 'smg/customer-api/models';
import { Shipments } from 'smg/customer-api/shipments';
import * as session from 'smg/customer-api/session';
import * as template from 'smg/template';
import { AccountReady } from '../account-session';
import { BindClearSearch, PageLoader } from '../account';
import { Relref } from '../hugo';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const $empty = document.querySelector('#tracking-empty') as HTMLElement;
    const $emptyTitle = document.querySelector('#tracking-empty-title') as HTMLElement;
    const $emptyBody = document.querySelector('#tracking-empty-body') as HTMLElement;
    const $noMatch = document.querySelector('#tracking-no-match') as HTMLElement;
    const $noMatchTitle = document.querySelector('#tracking-no-match-title') as HTMLElement;
    const $noMatchBody = document.querySelector('#tracking-no-match-body') as HTMLElement;

    BindClearSearch();

    let view: OrganizationView;
    let carriersMap: Awaited<ReturnType<typeof Carriers>>;

    const pl: PageLoader<models.Shipment> = new PageLoader<models.Shipment>({
        cards: { empty: $empty, noMatch: $noMatch },
        init: async () => {
            const user = await session.Session().then(s => s.user);
            view = await ResolveOrganizationView(user);
            RenderOrganizationSwitch(view, user);
            carriersMap = await Carriers();
        },
        get: () => Shipments({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'order,created,trackingNumber,carrierId,shippedAt,deliveredAt',
            p: pl.currentPage(),
            q: pl.search(),
        }, view.params()).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: shipment => {
            const shipmentEl = template.Clone('#trackingInformation').firstElementChild as HTMLElement;

            // Place order/invoice numbers and adding appending the order link
            const orderNumberField = shipmentEl.querySelector('.order-number') as HTMLElement;
            const orderNumberLink = shipmentEl.querySelector('.order-link') as HTMLAnchorElement;
            const order = shipment.order;
            if (!order) {
                throw new Error(`shipment ${shipment.id} missing order`);
            }
            orderNumberField.textContent = String(order.invoiceNumber);
            orderNumberLink.href = view.withLens(`${Relref('/orders/track')}?id=${order.id ?? ''}`);

            // Place order date. shipment.created
            const orderDate = shipmentEl.querySelector('.order-date') as HTMLElement;
            if (shipment.created.isValid) {
                orderDate.textContent = shipment.created.toLocaleString(DateTime.DATE_SHORT);
            }

            if (view.active) {
                const $attributionRow = shipmentEl.querySelector('.shipment-attribution-row') as HTMLElement;
                const $attribution = shipmentEl.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, order.userID ?? '');
                $attributionRow.classList.remove('d-none');
            }

            // Place tracking number
            const trackingNumberField = shipmentEl.querySelector('.shipment-tracking-number') as HTMLElement;
            trackingNumberField.textContent = shipment.trackingNumber;

            // Place the ship carrier
            const carrierFields = shipmentEl.querySelector('.shipment-carrier') as HTMLElement;
            if (shipment.carrierID) {
                carrierFields.textContent = carriersMap.get(shipment.carrierID)?.name || '';
            }

            // Place status field
            const statusField = shipmentEl.querySelector('.shipment-status') as HTMLElement;
            if (shipment.deliveredAt.isValid) {
                statusField.textContent = `Delivered ${shipment.deliveredAt.toLocaleString(DateTime.DATE_SHORT)}`;
            } else if (shipment.shippedAt.isValid) {
                statusField.textContent = `Shipped ${shipment.shippedAt.toLocaleString(DateTime.DATE_SHORT)}`;
            } else {
                statusField.parentElement?.parentElement?.remove();
            }

            // Place the href with appended link w/ tracking number. First determine the carrier link
            const trackingLinksField = shipmentEl.querySelector('.tracking-link') as HTMLAnchorElement;
            const carrier = carriersMap.get(shipment.carrierID);
            trackingLinksField.href = carrier?.trackingURL(shipment.trackingNumber) || '';

            return shipmentEl;
        },
        afterLoad: state => {
            // A failed init settles before `view` exists, so read it only
            // in the states a resolved view reaches.
            if (state === 'empty' && view.active) {
                const orgName = view.organization?.name ?? '';
                $emptyTitle.textContent = `No shipments for ${orgName} yet`;
                $emptyBody.textContent = 'This list fills in as members\' orders ship.';
                return;
            }

            if (state === 'no-match') {
                const orgName = view.organization?.name ?? '';
                const search = pl.search().trim();
                if (view.active) {
                    $noMatchTitle.textContent = `Nothing in ${orgName} matches "${search}"`;
                    $noMatchBody.textContent = `Clear the search to see every shipment in ${orgName}.`;
                } else {
                    $noMatchTitle.textContent = `No shipments match "${search}".`;
                }
            }
        },
    });

    pl.load();
}
